import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { v4 as newGuid } from 'uuid';
import {
  CORRELATION_ID,
  LoggingContextProps,
  LOGGING_CONTEXT,
  USE_CORRELATION_ID,
  LogContextKey,
} from '@nibtravel/shared/util/constants';
import {
  CardTokenResponse,
  IBrowserInfo,
  ICardToken,
  ICardTokenRequest,
  ICreditCardInput,
  IQuotePriceSummary,
  IThreeDSRequestInfo,
  NibTravelHttpError,
} from '@nibtravel/shared/util/models';
import {
  CardTokenEndpointService,
  QuotesApiEndpointService,
} from '@nibtravel/shared/util/url';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, map, tap, exhaustMap } from 'rxjs/operators';

import { ErrorHelper } from './helpers/error.helper';
import { QuoteHelperService } from './helpers/quote.helper';
import { LogEntry, LoggerService } from '@nibtravel/shared/util/logger';
import {
  IPaymentCorrelation,
  IPurchaseResponse,
  PurchaseRequest,
  IQuoteState,
} from './models';

@Injectable({ providedIn: 'root' })
export class QuoteDataService {
  private previousQuotePrices$ = new BehaviorSubject<
    IQuotePriceSummary[] | undefined
  >([]);
  constructor(
    private http: HttpClient,
    private store: Store,
    private endpointService: QuotesApiEndpointService,
    private tokenEndPointService: CardTokenEndpointService,
    private errorHelper: ErrorHelper,
    private quoteHelper: QuoteHelperService,
    private logger: LoggerService
  ) {}

  /**
   * Retrieve quote with the specified brand and quote identifiers
   * @param params
   */
  getQuote(params: {
    brand: string;
    quoteId: string;
    planId?: number;
    stage?: string;
  }): Observable<IQuoteState> {
    const correlationId = newGuid();

    const url = this.endpointService.getQuoteUrl(params.brand, params.quoteId);

    const context = new HttpContext()
      .set(CORRELATION_ID, correlationId)
      .set(LOGGING_CONTEXT, {
        [LoggingContextProps.Category]: 'Quote',
        [LoggingContextProps.Method]: 'QuoteDataService.getQuote',
        [LoggingContextProps.Message]: 'Find Quote',
        [LoggingContextProps.Context]: {
          planId: params.planId,
          stage: params.stage,
          [LogContextKey.QuoteIdToken]: params.quoteId,
        },
      });

    return this.http.get<IQuoteState>(url, { context }).pipe(
      map((quote) => this.mapQuoteResponse(quote, params.planId)),
      catchError((error) => this.handleError(error))
    );
  }

  /** Update a quote based on current quote input */
  update(
    quote: IQuoteState,
    brand: string,
    planId?: number
  ): Observable<IQuoteState> {
    const url = this.endpointService.getQuoteUrl(brand);

    const context = new HttpContext().set(LOGGING_CONTEXT, {
      [LoggingContextProps.Category]: 'Quote',
      [LoggingContextProps.Method]: 'QuoteDataService.update',
      [LoggingContextProps.Message]: 'Quote Update',
      [LoggingContextProps.Context]: {
        planId,
        [LogContextKey.QuoteIdToken]: quote?.id,
      },
    });

    return this.http.post<IQuoteState>(url, quote, { context }).pipe(
      map((quote) => this.mapQuoteResponse(quote, planId)),
      catchError((error) => this.handleError(error))
    );
  }

  emailQuote(
    quote: IQuoteState,
    brand: string,
    emailAddress: string
  ): Observable<any> {
    const url = this.endpointService.getQuoteCommunicationUrl(
      brand,
      quote.id ?? ''
    );

    const context = new HttpContext().set(LOGGING_CONTEXT, {
      [LoggingContextProps.Category]: 'EmailQuote',
      [LoggingContextProps.Method]: 'QuoteDataService.emailQuote',
      [LoggingContextProps.Message]: 'Email Quote',
      [LoggingContextProps.Context]: {
        [LogContextKey.QuoteIdToken]: quote?.id,
      },
    });

    return this.http
      .post(url, { emailAddress }, { context })
      .pipe(catchError((error) => this.handleError(error)));
  }

  /** Purchase a quote with card payment */
  purchase(
    quote: IQuoteState,
    creditCardInput: ICreditCardInput,
    brand: string,
    correlationId: string
  ): Observable<IPurchaseResponse & IPaymentCorrelation> {
    const card: ICardTokenRequest = {
      source: brand,
      transactionId: '',
      cardHolderName: creditCardInput.nameOnCard,
      pan: creditCardInput.cardNumber,
      expiryMonth: creditCardInput.expiryMonth,
      expiryYear: creditCardInput.expiryYear,
      cvv: creditCardInput.verificationNumber,
    };

    const context = new HttpContext()
      .set(CORRELATION_ID, correlationId)
      .set(LOGGING_CONTEXT, {
        [LoggingContextProps.Category]: 'Purchase',
        [LoggingContextProps.Method]: 'QuoteDataService.purchase',
        [LoggingContextProps.Message]: 'Quote Purchase',
        [LoggingContextProps.Context]: {
          [LogContextKey.QuoteIdToken]: quote?.id,
        },
      });

    this.validatePolicyHolder(quote, correlationId);

    return this.getCardToken(card, quote?.id ?? '').pipe(
      map((card) => this.mapCardTokenResponse(brand, quote, card.token)),
      exhaustMap((data) => this.doPurchase(brand, data, context)),
      tap((response) => {
        this.logger.info<LogEntry<string>>({
          category: 'Purchase',
          method: 'QuoteDataService.purchase',
          value: 'Quote Purchase',
          context: {
            [LogContextKey.QuoteIdToken]: quote?.id,
            [LogContextKey.PolicyNumber]: response?.quote?.policyNumber,
          },
          extraProps: {
            status: 'Success',
          },
          correlationId,
        });
      })
    );
  }

  /**
   * Verify the token obtained from 3ds shopper flow to complete the purchase
   *
   * @param quote The Quote object currently stored on state
   * @param threeDSRequestInfo The 3DS Request
   * @param cardToken The card token received from API
   * @param paymentIds
   * @param brand
   * @param correlationId The correlation Id to use
   */
  verifyPurchase(
    quote: IQuoteState,
    threeDSRequestInfo: IThreeDSRequestInfo,
    cardToken: ICardToken,
    paymentIds: string,
    brand: string,
    correlationId: string
  ): Observable<IPurchaseResponse> {
    const purchaseData: PurchaseRequest = {
      quote: quote,
      cardToken: cardToken,
      paymentIds: paymentIds,
      threeDSRequestInfo: {
        ...threeDSRequestInfo,
        browserInfo: this.getBrowserInfo(),
      },
    };

    const context = new HttpContext()
      .set(CORRELATION_ID, correlationId ?? newGuid())
      .set(LOGGING_CONTEXT, {
        [LoggingContextProps.Category]: 'Purchase',
        [LoggingContextProps.Method]: 'QuoteDataService.verifyPurchase',
        [LoggingContextProps.Message]: 'Verify Purchase',
        [LoggingContextProps.Context]: {
          [LogContextKey.QuoteIdToken]: quote.quoteId,
        },
      });

    return this.doPurchase(brand, purchaseData, context).pipe(
      tap((response) => {
        this.logger.info<LogEntry<string>>({
          category: 'Purchase',
          method: 'QuoteDataService.verifyPurchase',
          value: 'Verify Purchase',
          context: {
            [LogContextKey.QuoteIdToken]: quote?.id,
            [LogContextKey.PolicyNumber]: response?.quote?.policyNumber,
          },
          extraProps: {
            status: 'Success',
          },
          correlationId,
        });
      })
    );
  }

  private mapQuoteResponse(quote: IQuoteState, planId?: number): IQuoteState {
    const departureDate = this.quoteHelper.updateDisplayDateFormat(
      quote.input.departureDate
    );
    const coverEndDate = this.quoteHelper.updateDisplayDateFormat(
      quote.input.coverEndDate
    );

    return {
      ...quote,
      input: {
        ...quote.input,
        planId: planId,
        coverEndDate: coverEndDate,
        departureDate: departureDate,
      },
    };
  }

  private doPurchase(
    brand: string,
    data: PurchaseRequest,
    context: HttpContext
  ) {
    const url = this.endpointService.getPurchaseUrl(brand);
    return this.http
      .post<IPurchaseResponse>(url, data, {
        context,
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  private getBrowserInfo(): IBrowserInfo {
    return {
      color: window.screen.colorDepth.toString(),
      width: window.screen.width.toString(),
      height: window.screen.height.toString(),
      tzOffset: new Date().getTimezoneOffset().toString(),
      javaEnabled: navigator.javaEnabled().toString(),
    };
  }

  private getCardToken(card: ICardTokenRequest, quoteId: string) {
    const url = this.tokenEndPointService.getCardTokenUrl();
    const context = new HttpContext()
      .set(USE_CORRELATION_ID, false)
      .set(LOGGING_CONTEXT, {
        [LoggingContextProps.Category]: 'Purchase',
        [LoggingContextProps.Method]: 'QuoteDataService.getCardToken',
        [LoggingContextProps.Message]: 'Get Card Token',
        [LoggingContextProps.Context]: {
          [LogContextKey.QuoteIdToken]: quoteId,
        },
      });

    return this.http
      .post<CardTokenResponse>(url, card, { context })
      .pipe(
        concatMap((res) =>
          res.outcome === 'accepted' ? of(res) : throwError(res)
        )
      );
  }

  private mapCardTokenResponse(
    brandCode: string,
    preparedQuote: IQuoteState,
    cardToken: string
  ) {
    const browserInfo = this.getBrowserInfo();
    return {
      quote: preparedQuote,
      cardToken: {
        source: brandCode,
        token: cardToken,
      },
      threeDSRequestInfo: {
        browserInfo: browserInfo,
      },
    };
  }

  private handleError(error: NibTravelHttpError): Observable<any> {
    this.store.dispatch(this.errorHelper.errorAction(error));

    // TODO: Need to review this.
    // This is throwing so the purchase action can display the error on the ui
    // Not a good approach!!
    return throwError(error);
  }

  /**
   * Debugging Quote Policy Holders
   * @param quote
   * @param correlationId
   * @private
   */
  private validatePolicyHolder(
    quote: IQuoteState,
    correlationId: string
  ): void {
    const primaryPolicyHolders = quote.input.policyHolders.filter(
      (ph) => ph.isPrimary === true
    );

    if (primaryPolicyHolders.length === 1) {
      const primary = primaryPolicyHolders[0];

      if (isNullOrEmpty(primary.emailAddress)) {
        this.logger.warning({
          category: 'Purchase',
          method: 'QuoteDataService.validatePolicyHolder',
          value: 'Primary policyholder email address is empty',
          context: {
            [LogContextKey.QuoteIdToken]: quote.id,
            policyHolderCount: quote.input.policyHolders.length,
          },
          correlationId,
        });
      }
      if (isNullOrEmpty(primary.phoneNumber)) {
        this.logger.warning({
          category: 'Purchase',
          method: 'QuoteDataService.validatePolicyHolder',
          value: 'Primary policyholder phone number is empty',
          context: {
            [LogContextKey.QuoteIdToken]: quote.id,
            policyHolderCount: quote.input.policyHolders.length,
          },
          correlationId,
        });
      }
    } else if (primaryPolicyHolders.length > 1) {
      this.logger.warning({
        category: 'Purchase',
        method: 'QuoteDataService.validatePolicyHolder',
        value: 'More than one primary policy holder found',
        context: {
          [LogContextKey.QuoteIdToken]: quote.id,
          primaryPolicyHoldersCount: primaryPolicyHolders.length,
          policyHolderCount: quote.input.policyHolders.length,
        },
        correlationId,
      });
    } else {
      // No Primary PolicyHolder
      this.logger.warning({
        category: 'Purchase',
        method: 'QuoteDataService.validatePolicyHolder',
        value: 'No primary policy holder found',
        context: {
          [LogContextKey.QuoteIdToken]: quote.id,
          policyHolderCount: quote.input.policyHolders.length,
        },
        correlationId,
      });
    }
  }

  getPreviousQuotePrices() {
    return this.previousQuotePrices$.asObservable();
  }
  setPreviousQuotePrices(quotePrices: IQuotePriceSummary[]) {
    this.previousQuotePrices$.next(quotePrices);
  }
}

function isNullOrEmpty(value: string | undefined | null): boolean {
  if (value) {
    return typeof value === 'string' && value.trim().length === 0;
  }
  return true;
}
